import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CreateCustomerDialog from "@/components/app/CreateCustomerDialog";
import CustomerEditDialog from "@/components/app/CustomerEditDialog";
import CustomerHistoryDialog from "@/components/app/CustomerHistoryDialog";
import ImportCustomersDialog from "@/components/app/ImportCustomersDialog";
import ResetPasswordDialog from "@/components/app/ResetPasswordDialog";
import { toast } from "sonner";
import { Search, User, Mail, Phone, MapPin, Pencil, KeyRound, Trash2, Users, History } from "lucide-react";

export default function CustomersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: { query: q } });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao carregar clientes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const search = (e) => { e.preventDefault(); load(query); };

  const resetPw = async (id) => {
    try {
      const { data } = await api.post(`/customers/${id}/reset-password`);
      setResetResult(data);
      toast.success("Nova senha provisória gerada.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/customers/${toDelete.id}`);
      toast.success("Cliente removido.");
      setToDelete(null);
      load(query);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <form onSubmit={search} className="flex gap-2 flex-1 min-w-[220px] max-w-md">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nome, e-mail, telefone ou CPF..." data-testid="customers-search-input" />
          <Button type="submit" variant="outline" className="gap-1.5" data-testid="customers-search-button"><Search className="h-4 w-4" /></Button>
        </form>
        <div className="flex gap-2">
          <ImportCustomersDialog onImported={() => load(query)} />
          <CreateCustomerDialog onCreated={() => load(query)} />
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16" data-testid="customers-empty">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nenhum cliente encontrado.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="customers-list">
          {items.map((c) => (
            <Card key={c.id} className="border-slate-200" data-testid={`customer-${c.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-[#1e7be0]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{c.email}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone || "-"}</p>
                    {c.address?.city && (
                      <p className="text-xs text-slate-400 flex items-center gap-1 truncate"><MapPin className="h-3 w-3" />{[c.address.city, c.address.state].filter(Boolean).join("/")}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 pt-1">
                  <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => setEditing(c)} data-testid={`customer-edit-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setHistory(c)} data-testid={`customer-history-${c.id}`}>
                    <History className="h-3.5 w-3.5" /> Histórico
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => resetPw(c.id)} data-testid={`customer-reset-${c.id}`}>
                    <KeyRound className="h-3.5 w-3.5" /> Senha
                  </Button>
                  <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-500 flex-shrink-0" onClick={() => setToDelete(c)} data-testid={`customer-delete-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CustomerEditDialog customer={editing} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={() => load(query)} />
      <CustomerHistoryDialog customer={history} open={!!history} onOpenChange={(o) => !o && setHistory(null)} />
      <ResetPasswordDialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)} result={resetResult} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="customer-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              O cliente <b>{toDelete?.name}</b> será removido e perderá o acesso. Os chamados existentes são mantidos, mas ficam sem cliente vinculado. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="customer-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="customer-delete-confirm-button">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
